import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { useUi, toast } from '../../stores/ui.js';
import { useSession } from '../../stores/session.js';

/**
 * The bag.
 *
 * Server state throughout — the cart lives in the database so it survives a
 * device change and so checkout can price it authoritatively. Nothing about it
 * is duplicated into Zustand; the store holds only whether the panel is open.
 */

export const useCart = () => {
  const pincode = useSession((state) => state.pincode);

  return useQuery({
    queryKey: queryKeys.cart.detail(pincode || null),
    queryFn: ({ signal }) => api.get('/cart', { params: { pincode: pincode || undefined }, signal }),
    staleTime: 30 * 1000,
  });
};

/**
 * Every mutation replaces the cache with the server's answer rather than
 * patching it optimistically. A bag that shows a total the server disagrees
 * with is worse than one that takes 200ms to update, and the server is the only
 * thing that knows whether the last unit just sold.
 */
const useCartMutation = (mutationFn, { onDone } = {}) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (cart, variables) => {
      client.setQueriesData({ queryKey: queryKeys.cart.all }, cart);
      onDone?.(cart, variables);

      // Stock or price notices come back on the cart itself; surfacing them is
      // what stops a shopper reaching checkout confused.
      for (const notice of cart?.notices?.removed ?? []) {
        toast({ title: 'Removed from your bag', description: notice.reason, tone: 'danger', duration: 8000 });
      }
      for (const notice of cart?.notices?.adjusted ?? []) {
        toast({ title: 'Your bag changed', description: notice.reason, duration: 8000 });
      }
    },
    onError: (error) => {
      toast({
        title: 'That did not work',
        description: error instanceof ApiError ? error.message : 'Try again in a moment.',
        tone: 'danger',
      });
    },
  });
};

export const useAddToCart = () => {
  const openCart = useUi((state) => state.openCart);

  return useCartMutation(
    ({ productId, variantId, bottleId, quantity = 1 }) =>
      api.post('/cart/items', { productId, variantId: variantId ?? null, bottleId: bottleId ?? null, quantity }),
    {
      onDone: (cart, variables) => {
        // The line that was actually added, matched on what we asked for.
        // `items.at(-1)` is the last row in the bag, which is only the right one
        // when the product was not already in it — adding a second bottle of
        // something you already had named whichever product happened to sort
        // last instead.
        const line = cart?.items?.find(
          (item) =>
            item.productId === variables.productId &&
            (item.variantId ?? null) === (variables.variantId ?? null) &&
            (!variables.bottleId || item.bottle?._id === variables.bottleId),
        );

        toast({
          title: 'Added to your bag',
          description: line?.title,
          // Same option formatting the bag itself uses, so the toast and the
          // line the shopper opens next agree with each other.
          meta: line?.options?.length
            ? line.options.map((option) => option.value).join(' · ')
            : undefined,
          image: line?.image ?? undefined,
          tone: 'success',
          action: { label: 'View bag', onClick: openCart },
        });
      },
    },
  );
};

/**
 * Adds a mix-and-match set: the combo, one fragrance per bottle in the order
 * picked, and the bottle.
 */
export const useAddCombo = () => {
  const openCart = useUi((state) => state.openCart);

  return useCartMutation(
    ({ comboId, productIds, bottleId, quantity = 1 }) =>
      api.post('/cart/combos', { comboId, productIds, bottleId: bottleId ?? null, quantity }),
    {
      onDone: (cart, variables) => {
        const line = cart?.items?.find(
          (item) => item.comboId === variables.comboId && item.components?.map((c) => String(c.product)).join() === variables.productIds.join(),
        );
        toast({
          title: 'Combo added to your bag',
          description: line?.title,
          meta: line?.components?.map((component) => component.title).join(' · '),
          image: line?.image ?? undefined,
          tone: 'success',
          action: { label: 'View bag', onClick: openCart },
        });
      },
    },
  );
};

export const useUpdateCartItem = () =>
  useCartMutation(({ itemId, quantity }) => api.patch(`/cart/items/${itemId}`, { quantity }));

export const useRemoveCartItem = () =>
  useCartMutation(({ itemId }) => api.delete(`/cart/items/${itemId}`));

export const useClearCart = () => useCartMutation(() => api.delete('/cart'));

export const useApplyCoupon = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (code) => api.post('/cart/coupon', { code }),
    onSuccess: (cart) => {
      client.setQueriesData({ queryKey: queryKeys.cart.all }, cart);
      toast({
        title: `${cart.coupon?.code} applied`,
        description: cart.coupon?.description,
        tone: 'success',
      });
    },
    // Deliberately no toast on failure: the coupon field shows the reason
    // inline, and a shopper who mistyped a code should not have to read it twice.
  });
};

export const useRemoveCoupon = () =>
  useCartMutation(() => api.delete('/cart/coupon'), {
    onDone: () => toast({ title: 'Discount removed' }),
  });
